import GoogleIcon from "@assets/icons/google.svg?react";
import ThinkChinaLogo from "@assets/logo@2x.svg?react";
import ThinkChinaLogoOnDark from "@assets/logo-on-dark.png";
import LianheZaobaoLogo from "@assets/logo-zb.svg?react";
import LianheZaobaoLogoOnDark from "@assets/logo-zb-on-dark@2x.svg?react";
import Divider from "@components/Divider/Divider";
import InputBox from "@components/InputBox/InputBox";
import Link from "@components/Link";
import SocialIcons from "@components/SocialIcons";
import { ThemeEnum } from "@components/ThemeProvider/ThemeProvider";
import useGTMDispatch from "@src/app/hooks/useGTMDispatch";
import { useNewsletterSignUp } from "@src/app/hooks/useNewsletterSignUp";
import clsx from "clsx";
import { useLocation } from "react-router";

type FooterProps = {
  theme?: ThemeEnum;
  className?: string;
};

export default function Footer({
  theme = ThemeEnum.light,
  className,
}: FooterProps) {
  const location = useLocation();
  const sendDataToGTM = useGTMDispatch();
  const isVideo = theme === ThemeEnum.dark;
  const { value, setValue, success, error, onSubmit } =
    useNewsletterSignUp("default");
  const pageUrl =
    typeof window !== "undefined"
      ? window.location.href
      : `https://www.thinkchina.sg${location.pathname}${location.search}`;
  return (
    <footer
      data-testid="footer"
      className={clsx(
        "bg-background-secondary dark:bg-background-primary-on-dark flex w-full flex-col items-center py-0 lg:px-3",
        className,
      )}
    >
      <div className="container max-w-[1200px] py-4">
        <div className="box-border grid w-full grid-cols-1 items-start lg:grid-cols-2 lg:gap-[40px]">
          <div className="flex min-w-0 flex-col">
            <Link
              to={"/"}
              className="box-content h-[60px] w-[200px] shrink-0 px-3 py-4"
            >
              {isVideo ? (
                <img
                  src={ThinkChinaLogoOnDark}
                  alt="ThinkChina Logo"
                  className="size-full"
                />
              ) : (
                <ThinkChinaLogo className="size-full" />
              )}
            </Link>

            <p className="text-body-2 text-primary dark:text-primary-on-dark flex-1 px-3 py-4 whitespace-pre-wrap">
              ThinkChina is an English language e-magazine with a China focus
              and powered by Singapore Press Holdings&apos; flagship Chinese
              daily Lianhe Zaobao.
            </p>
          </div>

          <div className="flex min-w-0 flex-col">
            <div className="box-content flex min-w-0 gap-3 px-3 py-4 max-lg:flex-col-reverse max-lg:justify-start max-lg:gap-4! lg:h-[60px] lg:items-end lg:justify-end">
              <Link
                to="https://www.google.com/preferences/source?q=https://www.thinkchina.sg"
                className="text-button-tertiary-label text-button-1 bg-button-tertiary hover:bg-button-tertiary-hover flex w-max shrink-0 items-center gap-2 rounded-full px-3! py-1 whitespace-nowrap max-lg:my-4 max-lg:text-center lg:px-0 lg:text-left"
              >
                <GoogleIcon />
                Add ThinkChina as a preferred source
              </Link>

              <SocialIcons />
            </div>

            <div className="box-border flex w-full flex-col items-start px-3 py-4 max-lg:px-6">
              <h3 className="text-header-4 text-primary dark:text-primary-on-dark w-full whitespace-normal">
                Get the ThinkChina Weekly Newsletter
              </h3>
              <p className="text-body-2 text-secondary dark:text-secondary-on-dark w-full whitespace-normal">
                Insights on China, right in your mailbox.{" "}
                <span className="mt-3 max-lg:block lg:mt-0">Sign up now.</span>
              </p>
            </div>
            <div
              onFocusCapture={() => {
                sendDataToGTM({
                  event: "tcCustomEvent",
                  eventCategory: "newsletter",
                  eventAction: "focus weekly",
                  eventLabel: pageUrl,
                });
              }}
            >
              <InputBox
                variant="newsletter"
                className="dark:bg-background-primary-on-dark px-3 py-4 max-lg:px-6"
                onSubmit={(email) => {
                  sendDataToGTM({
                    event: "tcCustomEvent",
                    eventCategory: "newsletter",
                    eventAction: "submit weekly",
                    eventLabel: pageUrl,
                  });
                  void onSubmit(email);
                }}
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                }}
                success={success}
                error={error}
              />
            </div>
          </div>
        </div>

        <Divider />

        <div className="box-border flex w-full flex-col items-start justify-center gap-4 px-3 py-4">
          <Link
            to={"https://www.zaobao.com.sg/"}
            className="h-[48.6px] w-[158px] self-center lg:self-start"
          >
            {isVideo ? <LianheZaobaoLogoOnDark /> : <LianheZaobaoLogo />}
          </Link>

          <div className="flex w-full flex-col max-lg:flex-col-reverse lg:flex-row lg:gap-10">
            <p className="text-body-2 text-secondary dark:text-secondary-on-dark flex-1 whitespace-pre-wrap max-lg:px-3 max-lg:py-4 max-lg:text-center">
              Copyright © 2026 SPH Media Limited. Co. Regn. No. 202120748H. All
              rights reserved.
            </p>

            <FooterLinks />
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterLinks() {
  const links = [
    { to: "mailto:thinkchina@sph.com.sg", label: "Contact Us" },
    {
      to: "https://www.sph.com.sg/tnc/website?ref=footer-page",
      label: "Terms & Conditions",
    },
    {
      to: "https://www.sph.com.sg/tnc/pdpa?ref=footer-page",
      label: "Personal Data Protection Notice",
    },
  ];

  return (
    <div className="flex w-full flex-1 flex-col items-start justify-end gap-[62px] max-lg:items-center max-lg:px-3 max-lg:py-4 lg:items-end">
      <div className="flex flex-row flex-wrap items-start justify-center gap-5 lg:flex-nowrap lg:justify-end">
        {links.map((link) => (
          <Link
            key={link.to}
            to={link.to}
            className="text-body-2 text-secondary dark:text-secondary-on-dark shrink-0"
          >
            {link.label}
          </Link>
        ))}
      </div>
    </div>
  );
}
